import { Info } from 'lucide-react'

import type { CoverageBand, DataStatus } from '@/contracts'
import { cn } from '@/lib/utils'

import styles from './coverage.module.scss'

/**
 * Section-level pill. Renders only for `partial` coverage — i.e. some real
 * data exists but the section has gaps the user should notice. For mocked
 * or placeholder values (status `demo` / `pending`), use `DemoDataChip`
 * with `size="section"` instead; that path is wired into the demo-data
 * registry. `live` renders nothing.
 *
 * Per the contract's coverage policy: section-level data_status is a screen
 * concern surfaced as a section pill — committed in the screen docs, not as
 * a contract field.
 */
export function CoveragePill({
  status,
  note,
  className,
}: {
  status: DataStatus
  note?: string
  className?: string
}) {
  if (status !== 'partial') return null
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-md border bg-card px-2.5 py-1',
        'border-sentiment-warning text-sentiment-warning',
        className,
      )}
      role="status"
    >
      <Info className="size-4 shrink-0" aria-hidden />
      <span className="text-body-default text-foreground">Copertura parziale</span>
      {note && <span className="text-caption text-muted-foreground">— {note}</span>}
    </div>
  )
}

/**
 * Row-level coverage indicator. Shown only when a row entity carries
 * `coverage-status === 'parziale'`. Absent for `piena`.
 *
 * Renders as a compact pill that matches `DemoDataChip.inline` (dashed
 * outline, tertiary tone, small caps). The visual family is shared because
 * the `parziale` flag in practice fires when a bucket depends on a
 * demo-data registry field, so the underlying state is the same "no real
 * source yet" condition `DemoDataChip` marks at field level. The component
 * stays separate because the trigger lives at bucket level, not field
 * level, and the chip face carries `Copertura parziale` rather than
 * `Dati demo`. The full phrase and any caller-provided `note` ride on the
 * native `title` and `aria-label` so the meaning is preserved without
 * extra chrome.
 */
export function CoverageBadge({
  status,
  note,
  className,
}: {
  status: CoverageBand
  note?: string
  className?: string
}) {
  if (status === 'piena') return null
  const title = note ? `Copertura parziale dei dati. ${note}` : 'Copertura parziale dei dati'
  return (
    <span className={cn(styles.badge, className)} title={title} aria-label={title}>
      Copertura parziale
    </span>
  )
}
